import { useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd';

type TReorderDragItem = { id: string; scope: string };

type TParams = {
  /** react-dnd item type; a row only accepts rows of the same type. */
  type: string;
  id: string;
  /** A row only accepts a drop from a row with the same scope, e.g. the same list at the same order revision. */
  scope: string;
  canReorder: boolean;
  onReorder: (sourceId: string, targetId: string) => void;
};

/** Drag-and-drop for a reorderable table row: attach `rowRef` to the row and `handleRef` to its drag handle. */
export const useReorderableRow = ({ type, id, scope, canReorder, onReorder }: TParams) => {
  const [{ isDragging }, drag, preview] = useDrag({
    type,
    item: (): TReorderDragItem => ({ id, scope }),
    canDrag: canReorder,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  const [{ isOver }, drop] = useDrop({
    accept: type,
    canDrop: (source: TReorderDragItem) => canReorder && source.scope === scope && source.id !== id,
    drop: (source: TReorderDragItem) => onReorder(source.id, id),
    collect: (monitor) => ({ isOver: monitor.isOver() && monitor.canDrop() }),
  });
  const rowRef = useCallback(
    (node: HTMLElement | null) => {
      drop(node);
      preview(node);
    },
    [drop, preview]
  );
  const handleRef = useCallback(
    (node: HTMLElement | null) => {
      drag(node);
    },
    [drag]
  );
  return { rowRef, handleRef, isDragging, isOver };
};
